import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { notification } from 'antd'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
// import { addReplyToCommentById, setComments } from 'features/files-preview/model/slice/filesPreviewSlice'
import { assetsApi } from 'shared/api/assetsApi'
import { commentsApi } from 'shared/api/commentsApi'
import { notificationsApi } from 'shared/api/notificationsApi'
import { RootState } from 'shared/config/store'
// import { AI_BOT_USER_ID } from 'shared/constants/ai'
import { Notification, NotificationsResponse } from 'shared/types/notifications'
import { initWs } from 'shared/websocket/ws'

interface NotificationsState {
  isNotificationsPopoverOpened: boolean
  notifications: Notification[]
  loading: boolean
  error: string | null
  hasUnread: boolean
  currentFileData: any
  isFileDataLoading: boolean
  currentCommentData: any,
  currentReplyCommentData: any,
  currentActiveNotification: Notification | null
  previewError: string | null
}

const initialState: NotificationsState = {
  isNotificationsPopoverOpened: false,
  notifications: [],
  loading: true,
  error: null,
  hasUnread: false,
  currentFileData: null,
  currentCommentData: null,
  currentReplyCommentData: null,
  isFileDataLoading: false,
  currentActiveNotification: null,
  previewError: null
}

let wsHandler

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

async function pollBatchOperationUntilDone(
  userId: string,
  operationId: string,
  options?: { maxAttempts?: number, delayMs?: number }
): Promise<void> {
  const { maxAttempts = 30, delayMs = 1000 } = options || {}

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const response: any = await notificationsApi.checkBatchMarkNotificationsAsRead(String(userId), operationId)

    if (response?.state === 'done') {
      return
    }

    if (response?.state === 'failed' || response?.state === 'error') {
      throw new Error('Batch operation failed')
    }

    await sleep(delayMs)
  }

  throw new Error('Batch operation polling timed out')
}

export const subscribeNotificationsToWSThunk = createAsyncThunk(
  'notifications/subscribeNotificationsToWSThunk',
  async function ({ generateNotificationContent }: { generateNotificationContent: (notification: Notification) => React.ReactNode }, { dispatch }) {
    const ws = initWs()

    wsHandler = async ({ output }) => {
      notification.open({
        message: '',
        description: generateNotificationContent(output),
        placement: 'topRight',
        duration: 3,
        style: {
          width: 400,
          padding: '12px 16px',
        },
        className: 'custom-notification'
      })

      dispatch(addNotification(output))
    }

    ws.addEventListener('news', wsHandler)
  }
)

export const unubscribeNotificationsToWSThunk = createAsyncThunk(
  'notifications/unubscribeNotificationsToWSThunk',
  async function () {
    const ws = initWs()

    ws.removeEventListener('news', wsHandler)
  }
)

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchAll',
  async (_, { rejectWithValue, getState }) => {
    const state = getState() as RootState
    const userID = state.user.user.id

    try {
      const response = await notificationsApi.getNotifications(String(userID))

      return response as NotificationsResponse
    } catch (error) {
      return rejectWithValue('Не удалось загрузить уведомления')
    }
  }
)

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async ({ userId, notificationId }: { userId: number, notificationId: string }, { dispatch, rejectWithValue, getState }) => {
    const state = getState() as RootState
    const notifications = state.notifications.notifications

    try {
      await notificationsApi.markNotificationAsRead(String(userId), notificationId)

      const updatedNotifications = notifications.map(notification =>
        notification.id === notificationId ? { ...notification, is_viewed: true } : notification
      )

      dispatch(setNotifications(updatedNotifications))

      dispatch(fetchNotifications())
      return notificationId
    } catch (error) {
      return rejectWithValue('Не удалось отметить уведомление как прочитанное')
    }
  }
)

export const batchMarkNotificationsAsRead = createAsyncThunk(
  'notifications/batchMarkNotificationsAsRead',
  async ({ userId, notificationIds }: { userId: number, notificationIds: string[] }, { dispatch, rejectWithValue }) => {
    try {
      const rsp: any = await notificationsApi.batchMarkNotificationsAsRead(String(userId), notificationIds)

      await pollBatchOperationUntilDone(String(userId), rsp?.id)

      dispatch(fetchNotifications())
    } catch (error) {
      return rejectWithValue('Failed to mark notifications as read')
    }
  }
)

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllNotificationsAsRead',
  async ({ userId, notificationIds }: { userId: number, notificationIds: string[] }, { dispatch, rejectWithValue }) => {
    try {
      if (notificationIds.length === 1) {
        await dispatch(markNotificationAsRead({ userId, notificationId: notificationIds[0] }))
      }

      if (notificationIds.length > 1) {
        await dispatch(batchMarkNotificationsAsRead({ userId, notificationIds }))
      }

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: `All notifications marked as read`,
        message: 'Notifications marked as read successfully',
      })
    } catch (error) {
      return rejectWithValue('Failed to mark all notifications as read')
    }
  }
)

const mergeAssetPreviewsWithCurrentVersion = (assetInfo: any) => {
  if (assetInfo?._embedded?.previews?.sizes) {
    return assetInfo
  }

  const versions = assetInfo?._embedded?.versions

  if (!Array.isArray(versions) || versions.length === 0) {
    return assetInfo
  }

  const currentVersion = versions.find((version) => version?.is_current) || versions[0]
  const versionPreviews = currentVersion?._embedded?.previews

  if (!versionPreviews?.sizes) {
    return assetInfo
  }

  return {
    ...assetInfo,
    _embedded: {
      ...assetInfo._embedded,
      previews: versionPreviews,
    }
  }
}

const normalizeCommentId = (commentId?: string | number | null): string | null => {
  if (commentId === undefined || commentId === null) {
    return null
  }

  const commentIdStr = String(commentId)

  return commentIdStr === '' ? null : commentIdStr
}

const getNotificationMessage = (notification: Notification | null) => {
  if (!notification?.messages?.length) {
    return null
  }

  return notification.messages[0]
}

const buildFallbackCommentData = (notification: Notification | null, commentId: string | null) => {
  const message = getNotificationMessage(notification)

  if (!notification || !message) {
    return null
  }

  return {
    id: commentId ?? null,
    author_id: notification.initiator_id,
    author_name: notification.initiator_name,
    created: notification.created,
    message: message.text,
  }
}

export const fetchPreviewData = createAsyncThunk(
  'notifications/fetchFileData',
  async (
    { fileId, commentId, signal, callback }: { fileId: string, commentId: string | number | null, signal?: AbortSignal, callback?: (assetInfo: any, commentInfo: any) => void },
    { getState, dispatch, rejectWithValue }
  ) => {
    const state = getState() as RootState
    const userID = state.user.user.family_user_id
    const normalizedCommentId = normalizeCommentId(commentId)
    const fallbackCommentData = buildFallbackCommentData(state.notifications.currentActiveNotification, normalizedCommentId)

    dispatch(setIsFileDataLoading(true))
    dispatch(setPreviewError(null))

    try {
      const commentPromise = normalizedCommentId
        ? commentsApi.getCommentById(userID, fileId, normalizedCommentId, signal)
        : Promise.resolve(null)

      const [assetResult, commentResult] = await Promise.allSettled([
        assetsApi.getAssetInfo(userID, fileId, signal, { preview: true, preview_jwt: true }),
        commentPromise,
      ])

      if (assetResult.status === 'rejected') {
        const reason: any = assetResult.reason

        if (reason?.name === 'AbortError') {
          return
        }
        // WebStorm
        dispatch(setCurrentReplyCommentData(null))
        dispatch(setCurrentFileData(null))
        dispatch(setCurrentCommentData(null))
        dispatch(setPreviewError('Не удалось загрузить данные для превью'))

        const message = reason?.message || 'Failed to load notification preview'
        return rejectWithValue(message)
      }

      const assetInfo = assetResult.value
      const assetWithMergedPreview = mergeAssetPreviewsWithCurrentVersion(assetInfo)

      dispatch(setCurrentFileData(assetWithMergedPreview))

      let commentInfo: any = null

      if (commentResult.status === 'fulfilled') {
        commentInfo = commentResult.value

        if (commentInfo?.parent_id) {
          try {
            const replyInfo = await commentsApi.getCommentById(userID, fileId, String(commentInfo.parent_id), signal)
            dispatch(setCurrentReplyCommentData(replyInfo))
          } catch (replyError: any) {
            if (replyError?.name !== 'AbortError') {
              dispatch(setCurrentReplyCommentData(null))
            }
          }
        } else {
          dispatch(setCurrentReplyCommentData(null))
        }

        if (commentInfo) {
          dispatch(setCurrentCommentData(commentInfo))
        } else if (fallbackCommentData) {
          dispatch(setCurrentCommentData(fallbackCommentData))
          dispatch(setCurrentReplyCommentData(null))
        }
      } else if (commentResult.status === 'rejected') {
        const reason: any = commentResult.reason

        if (reason?.name === 'AbortError') {
          return
        }

        if (fallbackCommentData) {
          dispatch(setCurrentCommentData(fallbackCommentData))
          dispatch(setCurrentReplyCommentData(null))
        } else {
          dispatch(setCurrentCommentData(null))
          dispatch(setCurrentReplyCommentData(null))
        }
      } else {
        if (fallbackCommentData) {
          dispatch(setCurrentCommentData(fallbackCommentData))
          dispatch(setCurrentReplyCommentData(null))
        } else {
          dispatch(setCurrentCommentData(null))
          dispatch(setCurrentReplyCommentData(null))
        }
      }

      if (callback) {
        const commentForCallback = commentInfo || fallbackCommentData
        if (commentForCallback) {
          callback(assetWithMergedPreview, commentForCallback)
        }
      }

      return { assetInfo: assetWithMergedPreview, commentInfo: commentInfo || fallbackCommentData }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return
      }

      dispatch(setPreviewError('Не удалось загрузить данные для превью'))
      dispatch(setCurrentReplyCommentData(null))
      dispatch(setCurrentFileData(null))
      dispatch(setCurrentCommentData(null))

      const message = error?.message || 'Failed to load notification preview'
      return rejectWithValue(message)
    } finally {
      dispatch(setIsFileDataLoading(false))
    }
  }
)


export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    clearNotifications: (state) => {
      state.notifications = []
      state.hasUnread = false
    },
    setCurrentFileData: (state, action: PayloadAction<any>) => {
      state.currentFileData = action.payload
    },
    setIsFileDataLoading: (state, action: PayloadAction<boolean>) => {
      state.isFileDataLoading = action.payload
    },
    setCurrentCommentData: (state, action: PayloadAction<any>) => {
      state.currentCommentData = action.payload
    },
    setCurrentReplyCommentData: (state, action: PayloadAction<any>) => {
      state.currentReplyCommentData = action.payload
    },
    setIsNotificationsPopoverOpened: (state, action: PayloadAction<boolean>) => {
      state.isNotificationsPopoverOpened = action.payload
    },
    setNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.notifications = action.payload
    },
    setCurrentActiveNotification: (state, action: PayloadAction<Notification | null>) => {
      state.currentActiveNotification = action.payload
    },
    setPreviewError: (state, action: PayloadAction<string | null>) => {
      state.previewError = action.payload
    },
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload._embedded.news
        state.loading = false
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  }
})

export const {
  setCurrentFileData,
  setIsFileDataLoading,
  setCurrentCommentData,
  setCurrentReplyCommentData,
  setIsNotificationsPopoverOpened,
  setNotifications,
  setCurrentActiveNotification,
  setPreviewError,
  addNotification
} = notificationsSlice.actions

export default notificationsSlice.reducer
